// Thesis-feature components: account-level toggle, thesis editor modal,
// recent-theses list, and the after-fill unmatched-positions picker.
// Kept in one file because they share Supabase RPC wiring and live
// together on the account-detail page.
//
// Talks to:
//   - veritas.account_settings table directly for SELECT and UPDATE flips.
//   - veritas.set_thesis_flow_enabled(account_login, enabled) RPC for the
//     bootstrap upsert (first-ever flip on an account).
//   - veritas.create_thesis(...) RPC for new theses.
//   - veritas.attach_thesis_manual(thesis_id, position_ticket) RPC for
//     after-fill manual attach.
//   - veritas.list_unmatched_positions(account_login, lookback) RPC for
//     the after-fill picker.

import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2, Plus, X, Target, Shield, Clock, AlertCircle } from 'lucide-react';
import { veritas } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { Chip, Chips, Panel, PanelBody, PanelHead } from '@/components/ui';
import type {
  AccountSettings, CreateThesisResult, Thesis, ThesisVersion, UnmatchedPosition,
} from '@/types/veritas';

// ════════════════════════════════════════════════════════════════════════
// ThesisPanel — top-level surface on account-detail.
//
// States it surfaces:
//   - Loading (account_settings fetch in flight).
//   - Disabled (no row yet, or thesis_flow_enabled=false). Renders a quiet
//     "Enable thesis flow" CTA explaining the wedge.
//   - Enabled (thesis_flow_enabled=true). Renders "+ New thesis" button +
//     list of recent theses on this account with their match state.
// ════════════════════════════════════════════════════════════════════════
// Per-account collapse-state localStorage key. Survives reloads + survives
// switching between accounts (each account remembers its own state).
const COLLAPSE_KEY = (accountLogin: number) => `veritas.thesis-panel.collapsed.${accountLogin}`;

export function ThesisPanel({ accountLogin }: { accountLogin: number }) {
  const [settings, setSettings] = useState<AccountSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [theses, setTheses] = useState<ThesisRow[]>([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toggling, setToggling] = useState(false);

  // Lazily read the persisted collapse state on first render. Default
  // expanded so first-time visitors see the wedge surface; from then on,
  // user preference wins.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY(accountLogin)) === '1';
    } catch {
      return false;
    }
  });

  // Re-read collapse state if the account changes mid-session (the user
  // navigates between accounts without remounting the page).
  useEffect(() => {
    try {
      setCollapsed(localStorage.getItem(COLLAPSE_KEY(accountLogin)) === '1');
    } catch {
      /* localStorage unavailable — keep current state */
    }
  }, [accountLogin]);

  function toggleCollapsed() {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSE_KEY(accountLogin), next ? '1' : '0');
      } catch {
        /* best-effort persistence */
      }
      return next;
    });
  }

  // Initial fetch of settings + recent theses.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      veritas
        .from('account_settings')
        .select('*')
        .eq('account_login', accountLogin)
        .maybeSingle(),
      fetchRecentTheses(accountLogin),
    ]).then(([s, ts]) => {
      if (cancelled) return;
      setSettings((s.data as AccountSettings | null) ?? null);
      setTheses(ts);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [accountLogin]);

  async function flipFlag(next: boolean) {
    setToggling(true);
    setError(null);
    const { error: rpcErr } = await veritas.rpc('set_thesis_flow_enabled', {
      p_account_login: accountLogin,
      p_enabled: next,
    });
    if (rpcErr) {
      setError(rpcErr.message);
      setToggling(false);
      return;
    }
    // Re-fetch settings so the UI reflects what's actually in the DB.
    const r = await veritas
      .from('account_settings')
      .select('*')
      .eq('account_login', accountLogin)
      .maybeSingle();
    setSettings((r.data as AccountSettings | null) ?? null);
    setToggling(false);
  }

  async function reloadTheses() {
    const ts = await fetchRecentTheses(accountLogin);
    setTheses(ts);
  }

  if (loading) {
    return (
      <Panel>
        <PanelHead title="THESIS" />
        <PanelBody>
          <div className="flex items-center gap-2 text-[12px] text-text-mid">
            <Loader2 size={14} className="animate-spin" />
            Loading…
          </div>
        </PanelBody>
      </Panel>
    );
  }

  const enabled = !!settings?.thesis_flow_enabled;

  // Collapsed-state sub-line is more compact — surfaces just enough
  // signal (count of theses if enabled) without expanding to the full
  // wedge explainer. Same panel head, smaller content.
  const sub = collapsed
    ? enabled
      ? `${theses.length} ${theses.length === 1 ? 'thesis' : 'theses'} — click to expand`
      : 'disabled — click to expand'
    : enabled
      ? 'descriptive surface — written before, graded on close'
      : 'opt-in: pre-trade thesis + post-trade adherence grading';

  return (
    <>
      <Panel>
        <PanelHead
          title="THESIS"
          sub={sub}
          right={
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => { e.stopPropagation(); flipFlag(!enabled); }}
                disabled={toggling}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-[0.1em] transition-colors',
                  enabled
                    ? 'bg-green/10 text-green hover:bg-green/15'
                    : 'bg-bg-2 text-text-dim hover:text-text',
                  toggling && 'opacity-50',
                )}
              >
                {toggling && <Loader2 size={11} className="animate-spin" />}
                {enabled ? 'ENABLED' : 'DISABLED'}
              </button>
              <button
                onClick={toggleCollapsed}
                aria-label={collapsed ? 'Expand thesis panel' : 'Collapse thesis panel'}
                aria-expanded={!collapsed}
                className="rounded-md p-1 text-text-dim hover:bg-bg-2 hover:text-text"
              >
                {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
              </button>
            </div>
          }
        />
        {!collapsed && (
        <PanelBody>
          {error && (
            <div className="mb-3 flex items-start gap-2 rounded-md border border-red/30 bg-red/5 px-3 py-2 text-[11px] text-red">
              <AlertCircle size={13} className="mt-[1px] flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {!enabled ? (
            <div className="space-y-2 text-[12px] text-text-mid">
              <p>
                Veritas can ask you to write down a thesis <em>before</em> each manual trade —
                what you expect, what would invalidate it, where you'll exit. After the trade
                closes, Veritas grades how well you stuck to it. Independent of P&amp;L.
              </p>
              <p className="text-text-dim">
                Default off. Per-account flag — bot trades (magic ≠ 0) are excluded automatically.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-[11px] text-text-dim">
                  {theses.length === 0
                    ? 'No theses yet. Write one before your next manual trade.'
                    : `${theses.length} ${theses.length === 1 ? 'thesis' : 'theses'} on this account`}
                </div>
                <button
                  onClick={() => setEditorOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-md bg-green px-3 py-1.5 text-[11px] font-bold uppercase tracking-eyebrow text-black hover:bg-green/90"
                >
                  <Plus size={13} />
                  New thesis
                </button>
              </div>

              {theses.length > 0 && (
                <div className="overflow-hidden rounded-md border border-line-soft">
                  <table className="w-full text-[11.5px]">
                    <thead className="bg-bg-2 text-[9.5px] uppercase tracking-eyebrow text-text-dim">
                      <tr>
                        <th className="px-2.5 py-2 text-left">Symbol</th>
                        <th className="px-2.5 py-2 text-left">Side</th>
                        <th className="px-2.5 py-2 text-right">Entry → Stop / Tgt</th>
                        <th className="px-2.5 py-2 text-right">Hold</th>
                        <th className="px-2.5 py-2 text-left">Match</th>
                        <th className="px-2.5 py-2 text-left">Created</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-line-soft">
                      {theses.map((t) => (
                        <ThesisRowDisplay key={t.id} t={t} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </PanelBody>
        )}
      </Panel>

      {editorOpen && (
        <ThesisEditor
          accountLogin={accountLogin}
          onClose={() => setEditorOpen(false)}
          onCreated={() => {
            setEditorOpen(false);
            void reloadTheses();
          }}
        />
      )}
    </>
  );
}

// Joined row for the recent-theses table (parent + version 1 contract +
// optional match metadata).
type ThesisRow = Thesis & {
  v1: ThesisVersion;
};

async function fetchRecentTheses(accountLogin: number): Promise<ThesisRow[]> {
  // Fetch parent rows; PostgREST embeds `thesis_versions` rows via the
  // foreign key. We filter for version=1 inside the embed.
  const { data, error } = await veritas
    .from('thesis')
    .select('*, thesis_versions!inner(*)')
    .eq('account_login', accountLogin)
    .eq('thesis_versions.version', 1)
    .order('created_at', { ascending: false })
    .limit(20);

  if (error || !data) return [];
  return (data as Array<Thesis & { thesis_versions: ThesisVersion[] }>).map((row) => ({
    ...row,
    v1: row.thesis_versions[0],
  }));
}

function ThesisRowDisplay({ t }: { t: ThesisRow }) {
  const sideLabel = t.v1.side === 1 ? 'LONG' : 'SHORT';
  const sideTone = t.v1.side === 1 ? 'text-green' : 'text-red';
  const matchLabel = matchMethodLabel(t.match_method);
  return (
    <tr className="hover:bg-bg-2/40">
      <td className="px-2.5 py-2 num font-semibold">{t.v1.symbol}</td>
      <td className={cn('px-2.5 py-2 text-[10px] font-bold uppercase tracking-[0.1em]', sideTone)}>
        {sideLabel}
      </td>
      <td className="px-2.5 py-2 text-right num text-text-mid">
        {fmtPrice(t.v1.entry_price)} → {fmtPrice(t.v1.invalidation_price)} / {fmtPrice(t.v1.target_price)}
      </td>
      <td className="px-2.5 py-2 text-right num text-text-mid">{t.v1.expected_hold_minutes}m</td>
      <td className="px-2.5 py-2">
        {t.position_ticket ? (
          <span className="inline-flex items-center gap-1 rounded-[3px] bg-blue/10 px-2 py-[1px] text-[9.5px] font-bold uppercase tracking-[0.1em] text-blue">
            #{t.position_ticket} · {matchLabel}
          </span>
        ) : (
          <span className="text-[10.5px] text-text-dim">unmatched</span>
        )}
      </td>
      <td className="px-2.5 py-2 text-[10.5px] text-text-mid">
        {new Date(t.created_at).toLocaleString(undefined, {
          month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
        })}
      </td>
    </tr>
  );
}

function matchMethodLabel(mm: 0 | 1 | 2 | null): string {
  if (mm === 0) return 'auto';
  if (mm === 1) return 'manual';
  if (mm === 2) return 'after-fill';
  return '';
}

function fmtPrice(v: number | null): string {
  if (v === null || v === undefined) return '—';
  return Number(v).toString();
}

// ════════════════════════════════════════════════════════════════════════
// ThesisEditor — modal form for create_thesis.
// Capture-mode toggle:
//   - Before-fill (default): writes a thesis with no fill yet. The matcher
//     trigger picks it up when the manual entry fill lands.
//   - After-fill: shows the unmatched-positions picker; on submit, writes
//     the thesis and then attaches it to the picked position via the
//     manual-attach RPC. match_method ends up = 1 (manual) — the matcher
//     itself would have classified it as 2 if it had auto-resolved, but
//     after-fill capture via the picker is explicitly a manual op.
// ════════════════════════════════════════════════════════════════════════
type CaptureMode = 'before' | 'after';

export function ThesisEditor({
  accountLogin,
  onClose,
  onCreated,
}: {
  accountLogin: number;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [mode, setMode] = useState<CaptureMode>('before');
  const [symbol, setSymbol] = useState('');
  const [side, setSide] = useState<1 | -1>(1);
  const [entryPrice, setEntryPrice] = useState('');
  const [invalidationPrice, setInvalidationPrice] = useState('');
  const [targetPrice, setTargetPrice] = useState('');
  const [maxRiskR, setMaxRiskR] = useState('1.0');
  const [expectedHoldMinutes, setExpectedHoldMinutes] = useState('240');
  const [proveMeWrong, setProveMeWrong] = useState('');
  const [entryTrigger, setEntryTrigger] = useState('');

  // After-fill picker state.
  const [unmatched, setUnmatched] = useState<UnmatchedPosition[]>([]);
  const [selectedPosition, setSelectedPosition] = useState<UnmatchedPosition | null>(null);
  const [pickerLoading, setPickerLoading] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Load unmatched positions whenever the user switches to after-fill mode.
  useEffect(() => {
    if (mode !== 'after') return;
    setPickerLoading(true);
    veritas
      .rpc('list_unmatched_positions', {
        p_account_login: accountLogin,
        p_lookback: '24:00:00', // 24 hours
      })
      .then((r) => {
        setUnmatched((r.data as UnmatchedPosition[]) ?? []);
        setPickerLoading(false);
      });
  }, [mode, accountLogin]);

  // When the user picks a position in after-fill mode, prefill symbol/side
  // from the position so the thesis matches what they actually traded.
  function pickPosition(p: UnmatchedPosition) {
    setSelectedPosition(p);
    setSymbol(p.symbol);
    setSide(p.side);
    if (p.entry_price !== null) {
      setEntryPrice(String(p.entry_price));
    }
  }

  function clearPickedPosition() {
    setSelectedPosition(null);
  }

  // Validation matches the M1b CHECK constraints + s2-plan D1 required-fields.
  const validation = useMemo(() => {
    if (!symbol.trim()) return 'Symbol is required.';
    if (!proveMeWrong.trim()) return 'Prove-me-wrong is required.';
    if (!invalidationPrice.trim()) return 'Invalidation price is required (or use a rule — coming soon).';
    if (!targetPrice.trim()) return 'Target price is required (or use a rule — coming soon).';
    const r = Number(maxRiskR);
    if (!Number.isFinite(r) || r <= 0) return 'Max risk R must be > 0.';
    const hold = Number(expectedHoldMinutes);
    if (!Number.isInteger(hold) || hold <= 0) return 'Expected hold must be a positive integer (minutes).';
    if (mode === 'after' && !selectedPosition) return 'Pick the position this thesis belongs to.';
    return null;
  }, [symbol, proveMeWrong, invalidationPrice, targetPrice, maxRiskR, expectedHoldMinutes, mode, selectedPosition]);

  async function submit() {
    if (validation) {
      setError(validation);
      return;
    }
    setSubmitting(true);
    setError(null);
    setSuccessMsg(null);

    // Step 1: create the thesis (matcher fires inside the function, covers the
    // before-fill auto-window AND the after-fill-grace path).
    const { data, error: createErr } = await veritas.rpc('create_thesis', {
      p_account_login: accountLogin,
      p_symbol: symbol.trim().toUpperCase(),
      p_side: side,
      p_max_risk_r: Number(maxRiskR),
      p_expected_hold_minutes: Number(expectedHoldMinutes),
      p_prove_me_wrong: proveMeWrong.trim(),
      p_entry_price: parseOptionalNumber(entryPrice),
      p_invalidation_price: parseOptionalNumber(invalidationPrice),
      p_target_price: parseOptionalNumber(targetPrice),
      p_entry_trigger: entryTrigger.trim() || null,
    });

    if (createErr) {
      setError(createErr.message);
      setSubmitting(false);
      return;
    }

    const result = data as CreateThesisResult;

    // Step 2: if after-fill mode and the matcher didn't auto-link (e.g. window
    // missed because the position is older than after_fill_grace), do the
    // manual attach. mode='before' never needs this — the trigger handles it
    // when the fill lands.
    if (mode === 'after' && selectedPosition && !result.matched_position_ticket) {
      const { error: attachErr } = await veritas.rpc('attach_thesis_manual', {
        p_thesis_id: result.thesis_id,
        p_position_ticket: selectedPosition.position_ticket,
      });
      if (attachErr) {
        setError(`Thesis created but manual attach failed: ${attachErr.message}`);
        setSubmitting(false);
        return;
      }
      setSuccessMsg(`Thesis created and attached to position #${selectedPosition.position_ticket} (manual).`);
    } else if (result.matched_position_ticket) {
      const mmLabel = matchMethodLabel(result.match_method);
      setSuccessMsg(`Thesis created. Matched to position #${result.matched_position_ticket} (${mmLabel}).`);
    } else {
      setSuccessMsg('Thesis created. Waiting for a fill to match.');
    }

    setSubmitting(false);
    // Brief pause so the user sees the success line, then close.
    setTimeout(() => onCreated(), 1200);
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/60 p-4 sm:p-8"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[640px] overflow-hidden rounded-[10px] border border-line bg-bg-1 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-line-soft bg-bg-2 px-5 py-3">
          <h2 className="text-[12px] font-bold uppercase tracking-eyebrow text-text">New Thesis</h2>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-text-dim hover:bg-bg-1 hover:text-text"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          {/* Capture-mode toggle */}
          <div>
            <Label>Capture mode</Label>
            <Chips>
              <Chip active={mode === 'before'} onClick={() => setMode('before')}>
                Before-fill
              </Chip>
              <Chip active={mode === 'after'} onClick={() => setMode('after')} alt>
                After-fill
              </Chip>
            </Chips>
            <p className="mt-1.5 text-[10.5px] text-text-dim">
              {mode === 'before'
                ? 'Write the thesis before placing the trade. The matcher links it when the fill lands.'
                : 'Pick an unmatched position from the last 24h. Symbol + side prefill from it.'}
            </p>
          </div>

          {/* After-fill picker */}
          {mode === 'after' && (
            <div>
              <Label>Unmatched positions (last 24h)</Label>
              {pickerLoading ? (
                <div className="rounded-md border border-line-soft bg-bg-2 px-3 py-3 text-[11px] text-text-dim">
                  <Loader2 size={12} className="mr-2 inline animate-spin" />
                  Loading…
                </div>
              ) : unmatched.length === 0 ? (
                <div className="rounded-md border border-line-soft bg-bg-2 px-3 py-3 text-[11px] text-text-dim">
                  No unmatched manual fills in the last 24 hours. Switch to before-fill or place a trade first.
                </div>
              ) : (
                <UnmatchedPicker
                  positions={unmatched}
                  selected={selectedPosition}
                  onPick={pickPosition}
                  onClear={clearPickedPosition}
                />
              )}
            </div>
          )}

          {/* Structured fields */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field
              label="Symbol"
              required
              hint="e.g. EURUSD"
              value={symbol}
              onChange={setSymbol}
              uppercase
            />
            <div>
              <Label required>Side</Label>
              <Chips>
                <Chip active={side === 1} onClick={() => setSide(1)}>Long</Chip>
                <Chip active={side === -1} onClick={() => setSide(-1)} alt>Short</Chip>
              </Chips>
            </div>
            <Field label="Entry price" hint="optional — leave empty for breakout" value={entryPrice} onChange={setEntryPrice} />
            <Field label="Invalidation price" required icon={<Shield size={11} />} value={invalidationPrice} onChange={setInvalidationPrice} />
            <Field label="Target price" required icon={<Target size={11} />} value={targetPrice} onChange={setTargetPrice} />
            <Field label="Max risk (R)" required value={maxRiskR} onChange={setMaxRiskR} />
            <Field
              label="Expected hold"
              required
              hint="minutes"
              icon={<Clock size={11} />}
              value={expectedHoldMinutes}
              onChange={setExpectedHoldMinutes}
            />
          </div>

          {/* Free-text fields — the wedge surface */}
          <TextArea
            label="Prove me wrong"
            required
            hint="What would invalidate this setup, regardless of price?"
            placeholder="e.g. price closes below 1.0500 on H1 / DXY breaks 105.20 / VIX > 25"
            value={proveMeWrong}
            onChange={setProveMeWrong}
            rows={3}
          />
          <TextArea
            label="Entry trigger"
            hint="optional — what specifically gets you in?"
            value={entryTrigger}
            onChange={setEntryTrigger}
            rows={2}
          />

          {/* Status */}
          {error && (
            <div className="flex items-start gap-2 rounded-md border border-red/30 bg-red/5 px-3 py-2 text-[11px] text-red">
              <AlertCircle size={13} className="mt-[1px] flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          {successMsg && (
            <div className="rounded-md border border-green/30 bg-green/5 px-3 py-2 text-[11px] text-green">
              {successMsg}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-line-soft bg-bg-2 px-5 py-3">
          <button
            onClick={onClose}
            className="rounded-md px-3 py-1.5 text-[11px] font-semibold uppercase tracking-eyebrow text-text-mid hover:bg-bg-1 hover:text-text"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting || !!validation}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md bg-green px-3 py-1.5 text-[11px] font-bold uppercase tracking-eyebrow text-black transition-opacity',
              (submitting || !!validation) && 'cursor-not-allowed opacity-60',
              !submitting && !validation && 'hover:bg-green/90',
            )}
          >
            {submitting && <Loader2 size={11} className="animate-spin" />}
            Save thesis
          </button>
        </div>
      </div>
    </div>
  );
}

function parseOptionalNumber(s: string): number | null {
  const trimmed = s.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

// ── Field primitives ───────────────────────────────────────────────────
function Label({
  children,
  required = false,
}: {
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <label className="mb-1 block text-[10px] font-bold uppercase tracking-eyebrow text-text-dim">
      {children}
      {required && <span className="ml-1 text-amber">*</span>}
    </label>
  );
}

function Field({
  label,
  hint,
  value,
  onChange,
  required = false,
  uppercase = false,
  icon,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  uppercase?: boolean;
  icon?: React.ReactNode;
}) {
  return (
    <div>
      <Label required={required}>
        {icon && <span className="mr-1 inline-flex translate-y-[1px] items-center">{icon}</span>}
        {label}
      </Label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(uppercase ? e.target.value.toUpperCase() : e.target.value)}
        className="w-full rounded-md border border-line bg-bg-2 px-2.5 py-1.5 text-[12px] text-text outline-none focus:border-green/60 num"
      />
      {hint && <p className="mt-1 text-[10px] text-text-dim">{hint}</p>}
    </div>
  );
}

function TextArea({
  label,
  hint,
  placeholder,
  value,
  onChange,
  required = false,
  rows = 3,
}: {
  label: string;
  hint?: string;
  placeholder?: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  rows?: number;
}) {
  return (
    <div>
      <Label required={required}>{label}</Label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        className="w-full rounded-md border border-line bg-bg-2 px-2.5 py-2 text-[12px] text-text outline-none focus:border-green/60 leading-relaxed"
      />
      {hint && <p className="mt-1 text-[10px] text-text-dim">{hint}</p>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// UnmatchedPicker — list of unmatched manual positions for after-fill
// capture. Click to select; click again to clear.
// ════════════════════════════════════════════════════════════════════════
function UnmatchedPicker({
  positions,
  selected,
  onPick,
  onClear,
}: {
  positions: UnmatchedPosition[];
  selected: UnmatchedPosition | null;
  onPick: (p: UnmatchedPosition) => void;
  onClear: () => void;
}) {
  return (
    <div className="overflow-hidden rounded-md border border-line-soft">
      <ul className="divide-y divide-line-soft">
        {positions.map((p) => {
          const isSelected = selected?.position_ticket === p.position_ticket;
          return (
            <li key={p.position_ticket}>
              <button
                onClick={() => (isSelected ? onClear() : onPick(p))}
                className={cn(
                  'flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-[11px] transition-colors',
                  isSelected ? 'bg-green/10' : 'hover:bg-bg-2',
                )}
              >
                <span className="flex items-center gap-2">
                  <span className="num font-semibold">{p.symbol}</span>
                  <span className={cn('rounded-[3px] px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-[0.1em]',
                    p.side === 1 ? 'bg-green/10 text-green' : 'bg-red/10 text-red')}>
                    {p.side === 1 ? 'LONG' : 'SHORT'}
                  </span>
                  <span className="text-text-mid">#{p.position_ticket}</span>
                  <span className="text-text-dim">@ {fmtPrice(p.entry_price)}</span>
                </span>
                <span className="text-text-dim">
                  {new Date(p.entry_ts).toLocaleString(undefined, {
                    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                  })}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      {selected && (
        <div className="border-t border-line-soft bg-bg-2 px-3 py-2 text-[10.5px] text-text-mid">
          Picked position #{selected.position_ticket}. Symbol + side prefilled below.
          {' '}
          <button onClick={onClear} className="text-blue hover:underline">Clear</button>
        </div>
      )}
    </div>
  );
}
